import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn';

interface TabsContextValue {
  baseId: string;
  registerTrigger: (trigger: TabsTriggerRegistration) => () => void;
  setValue: (value: string) => void;
  value: string;
}

interface TabsTriggerRegistration {
  disabled: boolean;
  id: string;
  value: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (context === null) {
    throw new Error('Tabs components must be used within Tabs.');
  }
  return context;
}

export interface TabsProps {
  children: ReactNode;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  value?: string;
}

export function Tabs({ children, defaultValue = '', onValueChange, value: controlledValue }: TabsProps) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const [triggers, setTriggers] = useState<TabsTriggerRegistration[]>([]);
  const value = controlledValue ?? uncontrolledValue;
  const baseId = useId();

  const registerTrigger = useCallback((trigger: TabsTriggerRegistration) => {
    setTriggers((currentTriggers) => {
      const index = currentTriggers.findIndex(({ id }) => id === trigger.id);
      if (index === -1) {
        return [...currentTriggers, trigger];
      }
      const nextTriggers = [...currentTriggers];
      nextTriggers[index] = trigger;
      return nextTriggers;
    });
    return () => setTriggers((currentTriggers) => currentTriggers.filter(({ id }) => id !== trigger.id));
  }, []);

  const enabledTriggers = triggers.filter((trigger) => !trigger.disabled);
  const selectedValue = enabledTriggers.some((trigger) => trigger.value === value)
    ? value
    : (enabledTriggers[0]?.value ?? '');

  function setValue(nextValue: string) {
    if (controlledValue === undefined) {
      setUncontrolledValue(nextValue);
    }
    onValueChange?.(nextValue);
  }

  return <TabsContext.Provider value={{ baseId, registerTrigger, setValue, value: selectedValue }}>{children}</TabsContext.Provider>;
}

export type TabsListProps = Omit<HTMLAttributes<HTMLDivElement>, 'role'>;

export const TabsList = forwardRef<HTMLDivElement, TabsListProps>(function TabsList(
  { className, ...props },
  ref,
) {
  return <div className={cn('inline-flex h-9 items-center rounded-[var(--radius-control)] bg-[var(--background-muted)] p-1', className)} ref={ref} {...props} role="tablist" />;
});

export interface TabsTriggerProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-controls' | 'aria-selected' | 'id' | 'role' | 'tabIndex' | 'type'> {
  value: string;
}

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(function TabsTrigger(
  { className, onClick, onKeyDown, value, ...props },
  ref,
) {
  const { baseId, registerTrigger, setValue, value: activeValue } = useTabsContext();
  const triggerId = useId();
  const isActive = value === activeValue;

  useLayoutEffect(() => registerTrigger({ disabled: Boolean(props.disabled), id: triggerId, value }), [props.disabled, registerTrigger, triggerId, value]);

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    onKeyDown?.(event);
    if (event.defaultPrevented || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      return;
    }
    const tabs = Array.from(event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [])
      .filter((tab) => !tab.disabled);
    const currentIndex = tabs.indexOf(event.currentTarget);
    const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const nextTab = tabs[nextIndex];
    nextTab?.focus();
    nextTab?.click();
  }

  return (
    <button
      className={cn('inline-flex items-center justify-center whitespace-nowrap rounded-[var(--radius-control)] px-3 py-1 text-sm font-medium text-[var(--foreground-muted)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)] disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-[var(--background)] data-[state=active]:text-[var(--foreground)]', className)}
      data-state={isActive ? 'active' : 'inactive'}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          setValue(value);
        }
      }}
      onKeyDown={handleKeyDown}
      ref={ref}
      {...props}
      aria-controls={`${baseId}-${value}-panel`}
      aria-selected={isActive}
      id={`${baseId}-${value}-tab`}
      role="tab"
      tabIndex={isActive ? 0 : -1}
      type="button"
    />
  );
});

export interface TabsContentProps extends Omit<HTMLAttributes<HTMLDivElement>, 'aria-hidden' | 'aria-labelledby' | 'hidden' | 'id' | 'role' | 'tabIndex'> {
  value: string;
}

export const TabsContent = forwardRef<HTMLDivElement, TabsContentProps>(function TabsContent(
  { className, value, ...props },
  ref,
) {
  const { baseId, value: activeValue } = useTabsContext();
  const isActive = value === activeValue;
  return <div className={cn('mt-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]', className)} ref={ref} {...props} aria-hidden={isActive ? undefined : true} aria-labelledby={`${baseId}-${value}-tab`} hidden={!isActive} id={`${baseId}-${value}-panel`} role="tabpanel" tabIndex={0} />;
});
